import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'
import FileDownloader from '../../../../../components/FileDownloader/FileDownloader'
import FileUploader from '../../../../../components/FileUploader/FileUploader'
import Table from '../../../../../components/Table/Table'
import UnderlineHeading from '../../../../../components/UnderlineText/UnderlineHeading'
import Warning from '../../../../../components/Warning/Warning'
import { useDiscountContext } from '../../../../../utils/hooks/Context/DiscountContext'
import { useLabels } from '../../../../../utils/hooks/useLabels/useLabels'
import useShowDetails from '../../../hooks/useShowDetails'
import MassiveImage from '../../../../../assets/images/massive_discount.png'
import { faArrowDown } from '@fortawesome/free-solid-svg-icons'
import { DataLoadError } from '../../../../../components/DataLoadError/DataLoadError'
import { useLoadingContext } from '../../../../../utils/hooks/Context/LoadingContext'
import { REPOSITORY } from '../../../../../api/repository/Repository'
import { useRequestStatusContext } from '../../../../../utils/hooks/Context/RequestStatusContext'
import {
  cleanRut,
  isNoNominative,
  isValidRut,
} from '../../../../../utils/formats/validators'
const fileKeysToObjectKeys = {
  Monto: 'nMonto',
  Rut: 'sEmpleado',
}
const fileObjectFormat = (object) =>
  Object.keys(object).reduce((acc, el) => {
    if (el === 'Rut') {
      // Los identificadores NN no son RUT: se dejan tal cual (sin limpiar) para
      // no romper el correlativo; solo los RUT se limpian a dígitos + DV.
      const raw = object[el]
      return {
        ...acc,
        [fileKeysToObjectKeys[el]]: isNoNominative(raw)
          ? `${raw ?? ''}`.trim()
          : cleanRut(raw),
      }
    }
    return { ...acc, [fileKeysToObjectKeys[el]]: object[el] }
  }, {})
const fileDataFormat = (data) =>
  data.map((item) => ({
    ...fileObjectFormat(item),
    sTarjeta: '',
    sComentario: '',
  }))

const dataLoadErrorColumns = [
  {
    Header: 'Rut',
    accessor: 'sEmpleado',
  },
  {
    Header: 'Mensaje',
    accessor: 'sComentario',
  },
]

function MassiveDiscount() {
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()

  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()

  const { texts } = useLabels()
  const { employeesChangedReplace } = useDiscountContext()
  const { boxRef, onShowAmount, onCLose } = useShowDetails()
  const [data, setData] = useState([])

  const [showDataLoadError, setShowDataLoadError] = useState(false)
  const [dataLoadError, setDataLoadError] = useState()
  const [validated, setValidated] = useState(false)

  useEffect(() => {
    if (!data.length > 0) onCLose()
  }, [data])

  const handleFile = useCallback((fileData) => {
    setData([])
    employeesChangedReplace([])
    setValidated(false)
    if (fileData) {
      const formated = fileDataFormat(fileData)
      setData(formated)
    }
  }, [])

  const handleError = (response) => {
    handleCLoseLoading()
    handleSetTitle(response?.data?.error, response.status)
    handleChangeBody(
      <Button variant="primary" onClick={() => handleClose()}>
        Cerrar
      </Button>,
    )
    handleOpen()
  }

  const validateData = () => {
    if (data?.length <= 0) return

    // Validación local de RUT (el front exige "sin puntos y con guión").
    // Las filas con RUT inválido se marcan como error y NO se envían al backend.
    // Los identificadores NN (no nominativos) NO tienen dígito verificador:
    // pasan sin validar.
    const validRows = []
    const invalidRows = []
    data.forEach((element) => {
      if (isNoNominative(element.sEmpleado) || isValidRut(element.sEmpleado)) {
        validRows.push(element)
      } else {
        invalidRows.push({
          ...element,
          sComentario:
            'RUT inválido: revise el dígito verificador (sin puntos y con guión)',
        })
      }
    })

    if (validRows.length <= 0) {
      setDataLoadError(invalidRows)
      employeesChangedReplace([])
      setShowDataLoadError(true)
      setValidated(false)
      return
    }

    handleOpenLoading()
    REPOSITORY.wsEmpresa.discounts
      .validateDiscountMasive({
        bNotificacion: false,
        sCentroCosto: null,
        cEmpleados: validRows,
        sSucursal: null,
      })
      .then((res) => {
        const backendErrors = Array.isArray(res.data?.cDatosDescuento?.cEmpleados)
          ? res.data.cDatosDescuento.cEmpleados
          : []
        const allErrors = [...invalidRows, ...backendErrors]
        if (allErrors.length > 0) {
          setDataLoadError(allErrors)
          employeesChangedReplace([])
          setShowDataLoadError(true)
          setValidated(false)
        } else {
          employeesChangedReplace(validRows)
          setShowDataLoadError(false)
          setDataLoadError([])
          setValidated(true)
          onShowAmount()
        }
      })
      .catch((err) => {
        handleError(err)
      })
      .finally(() => handleCLoseLoading())
  }

  return (
    <Flex direction="column">
      <VStack alignItems="flex-start" spacing="16px">
        <Warning warningText={texts.massive_advertise} />
        <Image src={MassiveImage} />
        <Text variant="underline">* Ingresar rut sin puntos y con guión</Text>
        <Text variant="underline">* Ingresar solo números en el monto</Text>
      </VStack>
      <HStack spacing="82px" mt="20px">
        <FileDownloader
          linkToFile="/files/Amipass_Recarga.xlsx"
          icon={faArrowDown}
        />
        <Flex direction="column">
          <UnderlineHeading variant="h5" text={texts.upload_excel_template} />
          <Box mt="43px">
            <FileUploader handleFile={handleFile} />
          </Box>
        </Flex>
        <Box>
          <Button
            mt="90px"
            variant="gray"
            disabled={validated}
            onClick={validateData}
          >
            Verificar información
          </Button>
        </Box>
      </HStack>
      {data.length > 0 && (
        <Box mt="100px" ref={boxRef}>
          <Table
            columns={[
              { Header: 'Rut', accessor: 'sEmpleado' },
              { Header: 'Monto', accessor: 'nMonto' },
            ]}
            data={data}
          />
        </Box>
      )}
      <DataLoadError
        data={dataLoadError}
        show={showDataLoadError}
        columns={dataLoadErrorColumns}
        onClose={() => {
          setShowDataLoadError(false)
        }}
      />
    </Flex>
  )
}
export default MassiveDiscount
